import { PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useState, useEffect, Dispatch, SetStateAction } from "react";

interface MultiDomainEmailInputProps {
  universityEmailDomain: string;
  setUniversityEmailDomain: Dispatch<SetStateAction<string>>;
  domainError: string;
  setDomainError: Dispatch<SetStateAction<string>>;
}

export default function MultiDomainInput({
  universityEmailDomain,
  setUniversityEmailDomain,
  domainError,
  setDomainError,
}: MultiDomainEmailInputProps) {
  // Internal state for managing domains as an array
  const [domains, setDomains] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState("");
  const [inputError, setInputError] = useState("");

  // Parse comma-separated string to array when component mounts or prop changes
  useEffect(() => {
    if (universityEmailDomain) {
      const parsedDomains = universityEmailDomain
        .split(",")
        .map((domain) => domain.trim())
        .filter((domain) => domain.length > 0);
      setDomains(parsedDomains);
    } else {
      setDomains([]);
    }
  }, [universityEmailDomain]);

  // Helper function to update parent state
  const updateParentState = (newDomains: string[]) => {
    const domainString = newDomains.join(",");
    setUniversityEmailDomain(domainString);

    // Clear domain error when domains are valid
    if (newDomains.length > 0 && domainError) {
      setDomainError("");
    }
  };

  const validateDomain = (domain: string) => {
    if (!domain) return "Domain cannot be empty";
    if (!domain.startsWith("@")) return "Domain must start with @";
    if (domain.length < 5) return "Domain is too short"; // e.g., @a.b is minimum

    // Strict domain validation: requires @something.tld
    const domainRegex = /^@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
    if (!domainRegex.test(domain)) {
      return "Invalid domain format (use @example.com)";
    }

    if (domains.includes(domain)) return "Domain already exists";

    return null;
  };

  const handleAddDomain = () => {
    const trimmedInput = currentInput.trim();
    const error = validateDomain(trimmedInput);

    if (error) {
      setInputError(error);
      return;
    }

    const newDomains = [...domains, trimmedInput];
    setDomains(newDomains);
    updateParentState(newDomains);
    setCurrentInput("");
    setInputError("");
  };

  const handleRemoveDomain = (indexToRemove: number) => {
    const newDomains = domains.filter((_, index) => index !== indexToRemove);
    setDomains(newDomains);
    updateParentState(newDomains);
  };

  const handleInputChange = (e: any) => {
    const value = e.target.value;
    setCurrentInput(value);

    // Clear input error when user starts typing valid input
    if (inputError && value.trim()) {
      const error = validateDomain(value.trim());
      if (!error) {
        setInputError("");
      }
    }
  };

  const handleInputBlur = () => {
    if (currentInput.trim()) {
      handleAddDomain();
    }
  };

  return (
    <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
      <label htmlFor="university-domains" className="sm:pt-2">
        Allowed Email Domains{" "}
        <span className="text-gray-500">(must start with @)</span>
      </label>

      <div className="sm:col-span-2 space-y-3">
        {/* Input for new domain */}
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              id="university-domains"
              type="text"
              value={currentInput}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="@example.com"
              className={`w-full border px-2 py-1 dark:bg-darkbg dark:text-gray-200 text-sm rounded-lg transition-colors ${
                inputError || domainError
                  ? "border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500"
              }`}
            />
            {inputError && (
              <div className="flex items-center gap-1 mt-1 text-red-500 text-xs">
                <span>{inputError}</span>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleAddDomain}
            disabled={!currentInput.trim()}
            className="px-3 bg-entntblue hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-1 h-7"
          >
            <PlusIcon className="h-4" />
            Add
          </button>
        </div>
        {domains.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {domains.map((domain, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm"
              >
                <span>{domain}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveDomain(index)}
                  className="ml-1 p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800/50 rounded-full transition-colors"
                  aria-label={`Remove ${domain}`}
                >
                  <XMarkIcon className="h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Show domain error from parent validation */}
      {domainError && (
        <div className="flex items-center gap-1 text-red-500 text-xs">
          <span>{domainError}</span>
        </div>
      )}
    </div>
  );
}
